import React from 'react';
import { Link } from './LocaleLink';
import { GraduationCap, Gift, Users, PartyPopper, Briefcase, HelpCircle, ArrowRight } from 'lucide-react';

const CASES = [
  {
    icon: <GraduationCap size={22} className="text-blue-500" />,
    bg: 'bg-blue-50',
    border: 'border-blue-100 dark:border-blue-900/50',
    title: 'Education',
    body: 'Pick students, organize groups, choose classroom activities, and make decisions fair and fast.',
    ctaText: 'For Education',
    ctaHref: '/classroom-name-picker/',
  },
  {
    icon: <Gift size={22} className="text-pink-500" />,
    bg: 'bg-pink-50',
    border: 'border-pink-100 dark:border-pink-900/50',
    title: 'Giveaways',
    body: 'Pick winners for contests, giveaways, raffles, and events.',
    ctaText: 'For Giveaways',
    ctaHref: '/giveaway-wheel/',
  },
  {
    icon: <Users size={22} className="text-teal-500" />,
    bg: 'bg-teal-50',
    border: 'border-teal-100 dark:border-teal-900/50',
    title: 'Teams & Work',
    body: 'Randomly assign people, build groups, or decide who goes first.',
    ctaText: 'For Teams',
    ctaHref: '/team-picker/',
  },
  {
    icon: <PartyPopper size={22} className="text-purple-500" />,
    bg: 'bg-purple-50',
    border: 'border-purple-100 dark:border-purple-900/50',
    title: 'Fun & Games',
    body: 'Choose games, challenges, activities, players, or whose turn it is.',
    ctaText: 'For Fun',
    ctaHref: '/spin-the-bottle/',
  },
  {
    icon: <Briefcase size={22} className="text-orange-500" />,
    bg: 'bg-orange-50',
    border: 'border-orange-100 dark:border-orange-900/50',
    title: 'Work',
    body: 'Randomize presentations, tasks, meetings, and team decisions.',
    ctaText: 'For Work',
    ctaHref: '/random-team-generator/',
  },
  {
    icon: <HelpCircle size={22} className="text-gray-500 dark:text-gray-400" />,
    bg: 'bg-gray-100 dark:bg-gray-800',
    border: 'border-gray-200 dark:border-gray-700',
    title: 'Everyday Decisions',
    body: "Can't decide? Put your options on the wheel and let fate choose.",
    ctaText: 'Make a Decision',
    ctaHref: '/',
  },
];

const UseCasesSection: React.FC = () => (
  <section className="py-14 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* <!-- SEO: topical coverage and a crawlable link to the Team Picker guide. --> */}
      <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white text-center mb-4">
        More Ways to Use Your Random Wheel
      </h2>
      <p className="max-w-3xl mx-auto text-center text-gray-600 dark:text-gray-300 leading-relaxed mb-10">
        Use your random wheel for team picking, classroom activities, and everyday decision-making.
        Follow the <Link to="/team-picker/" className="text-violet-700 dark:text-violet-400 underline">team picker wheel guide</Link> to
        build groups, try the <Link to="/random-team-generator/" className="text-violet-700 dark:text-violet-400 underline">random team generator</Link> for
        automatic assignments, or add your own choices to decide what comes next.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {CASES.map((c, i) => (
          <div key={i} className="flex flex-col bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className={`w-12 h-12 rounded-xl ${c.bg} ${c.border} border flex items-center justify-center mb-4`}>
              {c.icon}
            </div>
            <h3 className="font-bold text-gray-900 dark:text-white text-lg mb-2">{c.title}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-6 flex-1">
              {c.body}
            </p>
            <Link
              to={c.ctaHref}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 transition-colors mt-auto"
            >
              {c.ctaText}
              <ArrowRight size={16} aria-hidden="true" className="rtl:-scale-x-100" />
            </Link>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default UseCasesSection;
